import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { PlusIcon, MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline'
import { apiClient } from '../../services/apiClient'

interface Operator {
  id: string
  operator_code: string
  name: string
  email: string
  phone?: string
  is_active: boolean
  commission_rate: number
  last_login_at?: string
  created_at: string
  total_users: number
  active_users: number
  total_commission: number
  balance: number
  recent_users: number
  recent_commission: number
}

interface OperatorsListResponse {
  operators: Operator[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
    has_more: boolean
  }
}

export default function OperatorsPage() {
  const navigate = useNavigate()
  const [operators, setOperators] = useState<Operator[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    has_more: false
  })

  const fetchOperators = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        sort_by: 'created_at',
        sort_order: 'desc'
      })

      const response = await apiClient.get<OperatorsListResponse>(`/admin-operators-list?${params}`)
      
      if (response.success && response.data) {
        setOperators(response.data.operators)
        setPagination(response.data.pagination)
      } else {
        setError(response.message || '获取运营商列表失败')
      }
    } catch (err) {
      setError('网络错误，请稍后重试')
      console.error('Fetch operators error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchOperators()
  }, [currentPage, searchTerm, statusFilter])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    fetchOperators()
  }

  const handleOperatorClick = (operatorId: string) => {
    navigate(`/operators/${operatorId}`)
  }

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: '超级管理员',
      finance: '财务管理员',
      audit: '审计管理员',
      operator: '运营商'
    }
    return labels[role] || role
  }

  const getRoleBadgeColor = (role: string) => {
    const colors: Record<string, string> = {
      admin: 'bg-purple-100 text-purple-800',
      finance: 'bg-blue-100 text-blue-800',
      audit: 'bg-yellow-100 text-yellow-800',
      operator: 'bg-green-100 text-green-800'
    }
    return colors[role] || 'bg-gray-100 text-gray-800'
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const formatPercent = (rate: number) => {
    return `${(rate * 100).toFixed(1)}%`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN')
  }

  if (loading && operators.length === 0) {
    return (
      <div className="page-content">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="page-content">
      {/* 页面标题和操作 */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="page-title">后台用户管理</h1>
          <p className="page-subtitle">管理平台后台用户账户（管理员、财务、审计、运营商）</p>
        </div>
        <button
          onClick={() => navigate('/operators/create')}
          className="btn btn-primary"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          创建用户
        </button>
      </div>

      {/* 搜索和筛选 */}
      <div className="card mb-6">
        <div className="card-body">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  搜索用户
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="名称或邮箱"
                    className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  用户状态
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="all">全部状态</option>
                  <option value="active">活跃</option>
                  <option value="inactive">禁用</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  type="submit"
                  className="w-full btn btn-secondary"
                >
                  <FunnelIcon className="h-4 w-4 mr-2" />
                  筛选
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}

      {/* 运营商列表 */}
      <div className="card">
        <div className="overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {operators.map((operator) => (
              <li key={operator.id}>
                <div
                  onClick={() => handleOperatorClick(operator.id)}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-blue-600">
                            {operator.name.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-gray-900">
                            {operator.name}
                          </p>
                          <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(operator.operator_code)}`}>
                            {getRoleLabel(operator.operator_code)}
                          </span>
                          <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            operator.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {operator.is_active ? '活跃' : '禁用'}
                          </span>
                        </div>
                        <div className="flex items-center mt-1 space-x-4">
                          <p className="text-sm text-gray-500">{operator.email}</p>
                          {operator.phone && (
                            <p className="text-sm text-gray-500">{operator.phone}</p>
                          )}
                          {operator.operator_code === 'operator' && (
                            <span className="text-sm text-blue-600 font-medium">
                              佣金率: {formatPercent(operator.commission_rate)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      {operator.operator_code === 'operator' ? (
                        // 运营商显示用户和佣金信息
                        <>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <div className="font-medium text-gray-900">
                                用户: {operator.total_users} ({operator.active_users}活跃)
                              </div>
                              <div className="text-gray-500">
                                本月新增: {operator.recent_users}
                              </div>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">
                                余额: {formatCurrency(operator.balance)}
                              </div>
                              <div className="text-gray-500">
                                总佣金: {formatCurrency(operator.total_commission)}
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        // 管理员显示权限信息
                        <div className="text-sm text-gray-500">
                          管理员账户
                        </div>
                      )}
                      <div className="text-sm text-gray-500 mt-1">
                        注册: {formatDate(operator.created_at)}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {operators.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-sm text-gray-500">暂无后台用户数据</p>
            </div>
          )}
        </div>

        {/* 分页 */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                disabled={currentPage === pagination.pages}
                className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  显示第 <span className="font-medium">{(currentPage - 1) * 20 + 1}</span> 到{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * 20, pagination.total)}
                  </span>{' '}
                  条，共 <span className="font-medium">{pagination.total}</span> 条记录
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    上一页
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    {currentPage} / {pagination.pages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                    disabled={currentPage === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    下一页
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}